import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export type CheckFrequencyUnit = 'DAILY';

/**
 * How the equipment refrigerates: compressor (classic on/off cycle) or
 * Peltier cell (wine coolers, silent mini-fridges). Drives which thermal
 * health metrics the insights endpoints compute. Backend column is NOT NULL
 * with a COMPRESSOR default, so the API always returns a value.
 */
export type CoolingTechnology = 'COMPRESSOR' | 'PELTIER';

export interface Equipment {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  checkFrequency: number;
  checkFrequencyUnit: CheckFrequencyUnit;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  /**
   * How the equipment refrigerates. The backend column is NOT NULL with a
   * COMPRESSOR default, so `/equipment` responses always carry it — but
   * synthetic views that rebuild this shape from other endpoints (e.g. the
   * temperature register's equipment-status) legitimately omit it. Optional
   * keeps those paths honest (`undefined` = "not carried by this view")
   * instead of forcing a possibly wrong default; consumers fall back to
   * 'COMPRESSOR'.
   */
  coolingTechnology?: CoolingTechnology;
  /**
   * Ambient probe linked to this equipment (Peltier ΔT measurement); null =
   * estimated ΔT. Same optionality as `coolingTechnology`.
   */
  ambientSensorId?: string | null;
  /**
   * Read the assigned profile from the equipment itself, never from the bound
   * sensor: an equipment can carry a profile while having no sensor, and that
   * state is unreachable — and so unfixable — if the UI can only see it through
   * a sensor.
   */
  foodPresetId?: string | null;
  foodPresetLabel?: string | null;
  foodPresetIsCustom?: boolean;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EquipmentInactivityPeriod {
  id: string;
  equipmentId: string;
  startedAt: string;
  endedAt: string | null;
  label: string | null;
}

export interface CreateEquipmentRequest {
  name: string;
  type: string;
  checkFrequency?: number;
  checkFrequencyUnit?: CheckFrequencyUnit;
  minTemperature?: number;
  maxTemperature?: number;
  foodPresetId?: string | null;
}

export interface UpdateEquipmentRequest {
  name?: string;
  type?: string;
  checkFrequency?: number;
  checkFrequencyUnit?: CheckFrequencyUnit;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  coolingTechnology?: CoolingTechnology;
  /**
   * Same semantics as `foodPresetId`: `null` unlinks the ambient sensor,
   * `undefined` leaves the association untouched (equipment.service backend).
   */
  ambientSensorId?: string | null;
  foodPresetId?: string | null;
}

/**
 * System presets: SSOT is the backend static catalog
 * (`backend/src/equipment/iot-food-presets.ts`) — `id` and `maxTemperature`
 * drive the i18n lookup (`equipment.foodPreset.<id>` / `.foods`) and form
 * value, labels stay i18n keys, never sent as text by the server.
 *
 * Tenant-declared presets (`isCustom: true`, backed by `TenantFoodPreset`)
 * have no i18n key: `label` and `foods` are the tenant's own text, rendered
 * as-is, plus `declaredByName`/`declaredAt` for the legal-responsibility
 * attribution shown next to the "custom profile" marker.
 */
export interface FoodPreset {
  id: string;
  maxTemperature: number;
  label: string;
  foods: string[];
  isCustom: boolean;
  declaredByName?: string;
  declaredAt?: string;
}

export interface CreateTenantFoodPresetRequest {
  label: string;
  foods: string[];
  maxTemperature: number;
  responsibilityAccepted: boolean;
  /**
   * The declaration text as it was shown to the user, in their language — not the
   * i18n key. The key's wording can change later; what the tenant legally signed
   * off on cannot.
   */
  declarationText: string;
}

export interface UpdateTenantFoodPresetRequest {
  label?: string;
  foods?: string[];
  maxTemperature?: number;
  /**
   * Required by the backend only when maxTemperature actually changes: a new
   * limit is a new legal declaration, not an edit to the old one. Sending them
   * on a label/foods-only edit is harmless and leaves the original declaration
   * untouched.
   */
  responsibilityAccepted?: boolean;
  declarationText?: string;
}

export interface IEquipmentService {
  findAll(): Promise<Equipment[]>;
  findById(id: string): Promise<Equipment>;
  create(data: CreateEquipmentRequest): Promise<Equipment>;
  update(id: string, data: UpdateEquipmentRequest): Promise<Equipment>;
  delete(id: string): Promise<void>;
  setActiveState(
    id: string,
    data: { active: boolean; label?: string | null },
  ): Promise<EquipmentInactivityPeriod | null>;
  listInactivityPeriods(id: string): Promise<EquipmentInactivityPeriod[]>;
  updateCurrentPeriodLabel(id: string, label: string | null): Promise<EquipmentInactivityPeriod>;
  listFoodPresets(): Promise<FoodPreset[]>;
  createFoodPreset(data: CreateTenantFoodPresetRequest): Promise<FoodPreset>;
  updateFoodPreset(id: string, data: UpdateTenantFoodPresetRequest): Promise<FoodPreset>;
  deleteFoodPreset(id: string): Promise<void>;
}

@Injectable()
class EquipmentService implements IEquipmentService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async findAll(): Promise<Equipment[]> {
    return this.httpService.get<Equipment[]>('equipment');
  }

  async findById(id: string): Promise<Equipment> {
    return this.httpService.get<Equipment>(`equipment/${id}`);
  }

  async create(data: CreateEquipmentRequest): Promise<Equipment> {
    return this.httpService.post<Equipment>('equipment', data);
  }

  async update(id: string, data: UpdateEquipmentRequest): Promise<Equipment> {
    return this.httpService.put<Equipment>(`equipment/${id}`, data);
  }

  async delete(id: string): Promise<void> {
    await this.httpService.delete(`equipment/${id}`);
  }

  async setActiveState(
    id: string,
    data: { active: boolean; label?: string | null },
  ): Promise<EquipmentInactivityPeriod | null> {
    return this.httpService.patch<EquipmentInactivityPeriod | null>(
      `equipment/${id}/active-state`,
      data,
    );
  }

  async listInactivityPeriods(id: string): Promise<EquipmentInactivityPeriod[]> {
    return this.httpService.get<EquipmentInactivityPeriod[]>(`equipment/${id}/inactivity-periods`);
  }

  async updateCurrentPeriodLabel(
    id: string,
    label: string | null,
  ): Promise<EquipmentInactivityPeriod> {
    return this.httpService.patch<EquipmentInactivityPeriod>(
      `equipment/${id}/inactivity-periods/current`,
      { label },
    );
  }

  async listFoodPresets(): Promise<FoodPreset[]> {
    return this.httpService.get<FoodPreset[]>('equipment/food-presets');
  }

  async createFoodPreset(data: CreateTenantFoodPresetRequest): Promise<FoodPreset> {
    return this.httpService.post<FoodPreset>('equipment/food-presets', data);
  }

  async updateFoodPreset(id: string, data: UpdateTenantFoodPresetRequest): Promise<FoodPreset> {
    return this.httpService.put<FoodPreset>(`equipment/food-presets/${id}`, data);
  }

  async deleteFoodPreset(id: string): Promise<void> {
    await this.httpService.delete(`equipment/food-presets/${id}`);
  }
}

export { EquipmentService };
